/**
 * Custom klinecharts overlays.
 *
 * `priceRangeMeasure` — TradingView-style Date and Price Range tool. Click
 * two points on the chart and it draws a translucent rectangle showing
 * the price change, percentage change, bar count, and time delta between
 * the two clicks.
 *
 * `volumeProfile` — fixed-range volume profile. Click two points to set
 * the time range; draws a horizontal volume histogram anchored to the
 * right edge of the chart pane, plus dashed VAH/VAL lines across the
 * selection and a label showing POC / VAH / VAL prices.
 */

import {
  registerOverlay,
  type OverlayFigure,
  type OverlayTemplate,
} from "klinecharts";
import { computeVolumeProfile } from "./volumeProfile";

/**
 * Track which overlay names we've already registered this module load so
 * we don't fire klinecharts warnings on hot-reload, but unlike a single
 * boolean guard this lets a newly-added overlay (like volumeProfile shipped
 * after priceRangeMeasure was already registered) still get registered.
 */
const registeredOverlays = new Set<string>();

function registerOnce(template: OverlayTemplate): void {
  if (registeredOverlays.has(template.name)) return;
  registeredOverlays.add(template.name);
  try {
    registerOverlay(template);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`klinecharts overlay '${template.name}' failed:`, e);
  }
}

export function registerCustomOverlays(): void {
  // ===========================================================================
  // tradeMarker — direction-aware annotation for BUY/SELL fill markers
  // ===========================================================================
  //
  // klinecharts ships `simpleAnnotation` for this use-case, but it
  // hardcodes the line going *up* from the anchor point. We anchor BUY
  // markers below the candle (and SELL markers above), so for BUY we need
  // the dashed line + arrow + label below the anchor too — otherwise the
  // line points back up through the candle and into the price area, which
  // is what the user reported.
  //
  // extendData shape:
  //   { text: string, direction: "up" | "down" }
  //
  // direction="up"   line/arrow/label go up from the anchor — used for
  //                  SELL markers anchored at the candle's high.
  // direction="down" line/arrow/label go down from the anchor — used for
  //                  BUY markers anchored at the candle's low.
  registerOnce({
    name: "tradeMarker",
    totalStep: 2,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    styles: { line: { style: "dashed" } },
    createPointFigures: ({ overlay, coordinates }) => {
      if (coordinates.length === 0) return [];
      const ext = (overlay.extendData ?? {}) as {
        text?: string;
        direction?: "up" | "down";
        level?: number;
      };
      const text = typeof ext.text === "string" ? ext.text : "";
      const direction = ext.direction === "down" ? "down" : "up";
      // Collision-avoidance lane (0 = closest to the candle). Each lane
      // lengthens the leader line by LANE_STEP so a cluster of markers stacks
      // its labels at distinct heights instead of drawing them on top of one
      // another. LANE_STEP exceeds the tallest label (a 2-line close marker)
      // so adjacent lanes never overlap.
      const level =
        typeof ext.level === "number" && ext.level > 0 ? ext.level : 0;
      const LANE_STEP = 34;
      const baseLineLen = 50;

      const startX = coordinates[0].x;
      const sign = direction === "down" ? +1 : -1;
      const startY = coordinates[0].y + sign * 6;
      const lineEndY = startY + sign * (baseLineLen + level * LANE_STEP);
      const arrowEndY = lineEndY + sign * 5;
      const baseline: "top" | "bottom" =
        direction === "down" ? "top" : "bottom";

      return [
        {
          type: "line",
          attrs: {
            coordinates: [
              { x: startX, y: startY },
              { x: startX, y: lineEndY },
            ],
          },
          ignoreEvent: true,
        },
        {
          type: "polygon",
          attrs: {
            coordinates: [
              { x: startX, y: lineEndY },
              { x: startX - 4, y: arrowEndY },
              { x: startX + 4, y: arrowEndY },
            ],
          },
          ignoreEvent: true,
        },
        {
          type: "text",
          attrs: { x: startX, y: arrowEndY, text, align: "center", baseline },
          ignoreEvent: true,
        },
      ];
    },
  });

  registerOnce({
    name: "priceRangeMeasure",
    totalStep: 3, // 2 points (start, end) + 1 finished state per klinecharts convention
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ overlay, coordinates }) => {
      if (coordinates.length < 2) return [];
      const [a, b] = coordinates;
      const x = Math.min(a.x, b.x);
      const y = Math.min(a.y, b.y);
      const w = Math.abs(b.x - a.x);
      const h = Math.abs(b.y - a.y);

      const points = overlay.points;
      const p0 = points[0];
      const p1 = points[1];
      const startPrice = Number(p0?.value ?? 0);
      const endPrice = Number(p1?.value ?? 0);
      const startTs = Number(p0?.timestamp ?? 0);
      const endTs = Number(p1?.timestamp ?? 0);
      const dataIdx0 = Number(p0?.dataIndex ?? 0);
      const dataIdx1 = Number(p1?.dataIndex ?? 0);

      const priceDelta = endPrice - startPrice;
      const pricePct =
        startPrice !== 0 ? (priceDelta / startPrice) * 100 : 0;
      const barCount = Math.abs(dataIdx1 - dataIdx0);
      const timeDeltaMs = Math.abs(endTs - startTs);
      const timeLabel = formatTimeDelta(timeDeltaMs);

      const isUp = priceDelta >= 0;
      const fillColor = isUp
        ? "rgba(5, 150, 105, 0.18)"
        : "rgba(220, 38, 38, 0.18)";
      const borderColor = isUp ? "#059669" : "#dc2626";
      const textColor = isUp ? "#065f46" : "#991b1b";

      const labelLines = [
        `${priceDelta >= 0 ? "+" : ""}${priceDelta.toFixed(2)} (${
          pricePct >= 0 ? "+" : ""
        }${pricePct.toFixed(2)}%)`,
        `${barCount} bar${barCount === 1 ? "" : "s"} · ${timeLabel}`,
      ];
      const label = labelLines.join("\n");

      // Place label inside the rectangle, near the top.
      const labelX = x + w / 2;
      const labelY = Math.min(a.y, b.y) + 4;

      return [
        // Rectangle fill + border
        {
          type: "rect",
          attrs: { x, y, width: w, height: h },
          styles: {
            style: "stroke_fill",
            color: fillColor,
            borderColor,
            borderSize: 1,
            borderStyle: "dashed",
          },
        },
        // Top/bottom price guide lines extending across rectangle width
        {
          type: "line",
          attrs: {
            coordinates: [
              { x, y: a.y },
              { x: x + w, y: a.y },
            ],
          },
          styles: { color: borderColor, size: 1, style: "dashed" },
        },
        {
          type: "line",
          attrs: {
            coordinates: [
              { x, y: b.y },
              { x: x + w, y: b.y },
            ],
          },
          styles: { color: borderColor, size: 1, style: "dashed" },
        },
        // Centered stats label
        {
          type: "text",
          attrs: {
            x: labelX,
            y: labelY,
            text: label,
            align: "center",
            baseline: "top",
          },
          styles: {
            color: textColor,
            backgroundColor: "#ffffff",
            borderColor,
            borderSize: 1,
            paddingLeft: 6,
            paddingRight: 6,
            paddingTop: 4,
            paddingBottom: 4,
            size: 11,
            weight: "bold",
          },
        },
      ];
    },
  });

  // ===========================================================================
  // volumeProfile — fixed-range volume profile
  // ===========================================================================
  registerOnce({
    name: "volumeProfile",
    totalStep: 3, // 2 points (start, end) + 1 finished state
    // Show klinecharts' built-in anchor circles on each point. Lets the user
    // drag the start / end points after the overlay is placed; klinecharts
    // updates overlay.points and re-invokes createPointFigures automatically.
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ chart, coordinates, overlay, bounding, yAxis }) => {
      if (coordinates.length < 2 || !yAxis) return [];
      const [a, b] = coordinates;
      const points = overlay.points;
      const p0 = points[0];
      const p1 = points[1];

      // Slice the bars by dataIndex. klinecharts sets dataIndex on every
      // click-point reliably; `timestamp` is *not* always populated, which
      // was making the previous timestamp-filter return zero bars and the
      // overlay silently render nothing.
      const all = chart.getDataList();
      const idx0Raw = p0?.dataIndex;
      const idx1Raw = p1?.dataIndex;
      let startIdx: number;
      let endIdx: number;
      if (
        typeof idx0Raw === "number" &&
        typeof idx1Raw === "number" &&
        Number.isFinite(idx0Raw) &&
        Number.isFinite(idx1Raw)
      ) {
        startIdx = Math.max(0, Math.min(idx0Raw, idx1Raw));
        endIdx = Math.min(all.length - 1, Math.max(idx0Raw, idx1Raw));
      } else {
        // Fallback: use timestamp if dataIndex isn't set (defensive — should
        // never hit for click-points but keeps us safe if klinecharts
        // changes their behavior).
        const startTs = Math.min(
          Number(p0?.timestamp ?? Number.NaN),
          Number(p1?.timestamp ?? Number.NaN),
        );
        const endTs = Math.max(
          Number(p0?.timestamp ?? Number.NaN),
          Number(p1?.timestamp ?? Number.NaN),
        );
        if (!Number.isFinite(startTs) || !Number.isFinite(endTs)) {
          // eslint-disable-next-line no-console
          console.warn(
            "[trade1] volumeProfile: no dataIndex or timestamp on points",
            p0,
            p1,
          );
          return [];
        }
        startIdx = all.findIndex((k) => k.timestamp >= startTs);
        endIdx = all.findIndex((k) => k.timestamp > endTs);
        if (startIdx < 0) startIdx = 0;
        if (endIdx < 0) endIdx = all.length - 1;
        else endIdx = Math.max(startIdx, endIdx - 1);
      }
      const within = all.slice(startIdx, endIdx + 1);
      if (within.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(
          "[trade1] volumeProfile: empty bar slice",
          startIdx,
          endIdx,
          "of",
          all.length,
        );
        return [];
      }

      // Per-instance params live on overlay.extendData. The double-click
      // edit popover in PriceChart writes to this via chart.overrideOverlay.
      // serverProfile, when present, is a backend-computed profile that
      // used the *finest* available timeframe (e.g. 1-minute bars) — that's
      // what makes single-candle profiles show varying volume across rows
      // (a single hour bar carries one OHLCV; 60 underlying minute bars
      // carry 60 separate price/volume points).
      const ext = (overlay.extendData ?? {}) as {
        numLevels?: number;
        valueAreaPct?: number;
        serverProfile?: {
          poc: number;
          vah: number;
          val: number;
          totalVolume: number;
          levels: { lo: number; hi: number; volume: number; mid: number }[];
        } | null;
      };
      const numLevels =
        Number.isFinite(ext.numLevels) && (ext.numLevels as number) >= 2
          ? Math.floor(ext.numLevels as number)
          : 30;
      const valueAreaPct =
        Number.isFinite(ext.valueAreaPct) &&
        (ext.valueAreaPct as number) > 0 &&
        (ext.valueAreaPct as number) <= 1
          ? (ext.valueAreaPct as number)
          : 0.7;
      const profile =
        ext.serverProfile != null
          ? ext.serverProfile
          : computeVolumeProfile(
              within.map((k) => ({
                high: k.high,
                low: k.low,
                close: k.close,
                volume: k.volume ?? 0,
              })),
              numLevels,
              valueAreaPct,
            );
      if (!profile) return [];

      const xLeft = Math.min(a.x, b.x);
      const xRight = Math.max(a.x, b.x);

      // Histogram: anchor to the right edge of the selection, growing left.
      // Cap max bar width so the POC takes ~25% of the *chart* width.
      const maxBarWidth = bounding.width * 0.25;
      const peak = Math.max(...profile.levels.map((l) => l.volume));
      const widthFor = (vol: number) =>
        peak > 0 ? (vol / peak) * maxBarWidth : 0;

      const figures: OverlayFigure[] = [];

      // Find which bucket holds POC for highlighting.
      const pocBucketIdx = profile.levels.findIndex(
        (lv) => profile.poc >= lv.lo && profile.poc <= lv.hi,
      );

      // yAxis.convertToPixel(price) — simple price-to-y mapping.
      // klinecharts' RectStyle requires borderColor / borderSize / borderStyle
      // even when style is "fill" — omitting them makes the figure draw a
      // no-op (rendered with 0-alpha or rejected silently).
      for (let i = 0; i < profile.levels.length; i++) {
        const lv = profile.levels[i];
        const yHi = yAxis.convertToPixel(lv.hi);
        const yLo = yAxis.convertToPixel(lv.lo);
        if (!Number.isFinite(yHi) || !Number.isFinite(yLo)) continue;
        const top = Math.min(yHi, yLo);
        const h = Math.max(1, Math.abs(yLo - yHi));
        const w = widthFor(lv.volume);
        if (w <= 0) continue;
        const isPoc = i === pocBucketIdx;
        const inValueArea = lv.mid >= profile.val && lv.mid <= profile.vah;
        const color = isPoc
          ? "rgba(220, 38, 38, 0.55)"
          : inValueArea
            ? "rgba(99, 102, 241, 0.45)"
            : "rgba(99, 102, 241, 0.22)";
        figures.push({
          type: "rect",
          attrs: {
            x: xRight - w,
            y: top,
            width: w,
            height: Math.max(1, h - 1),
          },
          styles: {
            style: "fill",
            color,
            borderColor: "transparent",
            borderSize: 0,
            borderStyle: "solid",
          },
        });
        const volLabel = volumeLabelFigure(xRight - w, top + h / 2, h, lv.volume);
        if (volLabel) figures.push(volLabel);
      }

      // VAH / VAL / POC horizontals spanning the selection.
      const yVah = yAxis.convertToPixel(profile.vah);
      const yVal = yAxis.convertToPixel(profile.val);
      const yPoc = yAxis.convertToPixel(profile.poc);
      const dashedLine = (y: number, color: string, dashed: boolean) => ({
        type: "line" as const,
        attrs: {
          coordinates: [
            { x: xLeft, y },
            { x: xRight, y },
          ],
        },
        styles: {
          color,
          size: 1,
          style: dashed ? ("dashed" as const) : ("solid" as const),
          dashedValue: dashed ? [4, 4] : [],
        },
      });
      if (Number.isFinite(yVah)) {
        figures.push(dashedLine(yVah, "#a855f7", true));
        figures.push(levelTagFigure(xRight, yVah, `VAH ${fmtPrice(profile.vah)}`, "#a855f7", "bottom"));
      }
      if (Number.isFinite(yVal)) {
        figures.push(dashedLine(yVal, "#a855f7", true));
        figures.push(levelTagFigure(xRight, yVal, `VAL ${fmtPrice(profile.val)}`, "#a855f7", "top"));
      }
      if (Number.isFinite(yPoc)) figures.push(dashedLine(yPoc, "#dc2626", false));

      // Label box (top-left of selection): POC / VAH / VAL prices, plus the
      // timeframe the profile was built from. When that TF equals the chart's
      // own TF (no finer bars in the store for this window), a single-candle
      // profile is necessarily flat — the label tells the user to backfill a
      // finer TF (e.g. 1m) for a detailed distribution.
      const usedTf =
        typeof (ext as { usedTimeframe?: string }).usedTimeframe === "string"
          ? (ext as { usedTimeframe?: string }).usedTimeframe
          : null;
      const labelLines = [
        `POC ${fmtPrice(profile.poc)}`,
        `VAH ${fmtPrice(profile.vah)}`,
        `VAL ${fmtPrice(profile.val)}`,
        ...(usedTf ? [`TF ${usedTf}`] : []),
      ];
      figures.push({
        type: "text",
        attrs: {
          x: xLeft + 4,
          y: Math.min(a.y, b.y) + 4,
          text: labelLines.join("\n"),
          align: "left",
          baseline: "top",
        },
        styles: {
          color: "#1e293b",
          backgroundColor: "#ffffff",
          borderColor: "#a855f7",
          borderSize: 1,
          paddingLeft: 6,
          paddingRight: 6,
          paddingTop: 4,
          paddingBottom: 4,
          size: 11,
          weight: "bold",
        },
      });

      return figures;
    },
  });

  // ===========================================================================
  // weeklyVolumeProfile — auto-drawn per-ISO-week volume profile + HTP marker
  // ===========================================================================
  //
  // A debug overlay for the weekly_htp_revert strategy. One click on the
  // toolbar button drops a single overlay that walks every bar on the
  // chart, groups them by ISO week, computes a volume profile per week,
  // and draws — for each week:
  //   * a translucent right-anchored histogram inside that week's x range
  //   * a solid horizontal POC line spanning the week (the "HTP")
  //   * a small "HTP <price>" label at the right edge
  //
  // klinecharts overlays need at least one click-point per their
  // protocol, so we still register totalStep=1 and let the toolbar
  // helper place a dummy point; we never read its coordinates — figures
  // are always derived from chart.getDataList().
  registerOnce({
    name: "weeklyVolumeProfile",
    totalStep: 1,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ chart, overlay, yAxis, bounding }) => {
      if (!yAxis) return [];
      const all = chart.getDataList();
      if (all.length === 0) return [];

      const ext = (overlay.extendData ?? {}) as {
        numLevels?: number;
        period?: "daily" | "weekly" | "monthly" | "quarterly";
        serverWeeks?: Array<{
          startMs: number;
          endMs: number;
          poc: number;
          vah?: number;
          val?: number;
          levels: { lo: number; hi: number; volume: number }[];
          developing?: { ms: number; poc: number }[];
        }> | null;
      };
      const numLevels =
        Number.isFinite(ext.numLevels) && (ext.numLevels as number) >= 10
          ? Math.floor(ext.numLevels as number)
          : 30;
      const period = ext.period ?? "weekly";

      // Two render modes:
      //   1. Server profiles available (ext.serverWeeks set) — match the
      //      manual volumeProfile overlay's data source (finest TF in
      //      DuckDB), so both overlays agree on POC values.
      //   2. Fallback: local compute from chart's displayed bars. Used
      //      while the server fetch is in flight or if it failed.
      type RenderWeek = {
        poc: number;
        vah?: number;
        val?: number;
        levels: { lo: number; hi: number; volume: number }[];
        firstBarMs: number;
        lastBarMs: number;
        developing?: { ms: number; poc: number }[];
      };
      const weeks: RenderWeek[] = [];

      if (ext.serverWeeks && ext.serverWeeks.length > 0) {
        // For each server week, locate the chart bars that fall inside
        // [startMs, endMs) so the histogram column is anchored to the
        // bars visible on screen (and skips empty weeks gracefully).
        for (const w of ext.serverWeeks) {
          let firstBarMs = -1;
          let lastBarMs = -1;
          for (const b of all) {
            if (b.timestamp >= w.startMs && b.timestamp < w.endMs) {
              if (firstBarMs < 0) firstBarMs = b.timestamp;
              lastBarMs = b.timestamp;
            }
          }
          if (firstBarMs < 0) continue;
          weeks.push({
            poc: w.poc,
            vah: w.vah,
            val: w.val,
            levels: w.levels,
            firstBarMs,
            lastBarMs,
            developing: w.developing,
          });
        }
      } else {
        // Group bars by the requested period. The current bucket may be
        // partial; we still render its profile so the user can see what's
        // developing. Used only as a fallback while the server fetch is
        // in flight — the authoritative figures come from extendData
        // once it lands.
        const localPeriodKey = (ms: number): string => {
          const d = new Date(ms);
          if (period === "daily") {
            return `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
          }
          if (period === "monthly") {
            return `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
          }
          if (period === "quarterly") {
            return `${d.getUTCFullYear()}-${Math.floor(d.getUTCMonth() / 3)}`;
          }
          // weekly (default)
          const dow = (d.getUTCDay() + 6) % 7;
          const monday = new Date(d);
          monday.setUTCDate(d.getUTCDate() - dow);
          return `${monday.getUTCFullYear()}-${monday.getUTCMonth()}-${monday.getUTCDate()}`;
        };
        type Group = {
          key: string;
          bars: typeof all;
        };
        const groups: Group[] = [];
        let curr: Group | null = null;
        for (const b of all) {
          const key = localPeriodKey(b.timestamp);
          if (!curr || curr.key !== key) {
            curr = { key, bars: [] };
            groups.push(curr);
          }
          curr.bars.push(b);
        }
        for (const g of groups) {
          const profile = computeVolumeProfile(
            g.bars.map((k) => ({
              high: k.high,
              low: k.low,
              close: k.close,
              volume: k.volume ?? 0,
            })),
            numLevels,
          );
          if (!profile) continue;
          weeks.push({
            poc: profile.poc,
            vah: profile.vah,
            val: profile.val,
            levels: profile.levels,
            firstBarMs: g.bars[0].timestamp,
            lastBarMs: g.bars[g.bars.length - 1].timestamp,
          });
        }
      }

      const figures: OverlayFigure[] = [];

      for (const week of weeks) {
        // x range for this week.
        const leftRaw = chart.convertToPixel(
          { timestamp: week.firstBarMs },
          { paneId: "candle_pane" },
        );
        const rightRaw = chart.convertToPixel(
          { timestamp: week.lastBarMs },
          { paneId: "candle_pane" },
        );
        const leftCoord = Array.isArray(leftRaw) ? leftRaw[0] : leftRaw;
        const rightCoord = Array.isArray(rightRaw) ? rightRaw[0] : rightRaw;
        const xLeft = Number(leftCoord?.x);
        const xRight = Number(rightCoord?.x);
        if (!Number.isFinite(xLeft) || !Number.isFinite(xRight)) continue;
        const span = xRight - xLeft;
        if (span <= 0) continue;
        const profile = {
          poc: week.poc,
          vah: week.vah,
          val: week.val,
          levels: week.levels,
        };
        const hasValueArea =
          Number.isFinite(profile.vah) && Number.isFinite(profile.val);

        const peakVol = Math.max(...profile.levels.map((l) => l.volume));
        // Cap each week's histogram at 60% of its own x span (so the
        // longest bar fits comfortably inside the week's column).
        const maxBarWidth = span * 0.6;
        const widthFor = (vol: number) =>
          peakVol > 0 ? (vol / peakVol) * maxBarWidth : 0;

        const pocBucketIdx = profile.levels.findIndex(
          (lv) => profile.poc >= lv.lo && profile.poc <= lv.hi,
        );

        for (let i = 0; i < profile.levels.length; i++) {
          const lv = profile.levels[i];
          const yHi = yAxis.convertToPixel(lv.hi);
          const yLo = yAxis.convertToPixel(lv.lo);
          if (!Number.isFinite(yHi) || !Number.isFinite(yLo)) continue;
          const top = Math.min(yHi, yLo);
          const h = Math.max(1, Math.abs(yLo - yHi));
          const w = widthFor(lv.volume);
          if (w <= 0) continue;
          const isPoc = i === pocBucketIdx;
          const mid = (lv.lo + lv.hi) / 2;
          const inValueArea =
            hasValueArea &&
            mid >= (profile.val as number) &&
            mid <= (profile.vah as number);
          figures.push({
            type: "rect",
            attrs: {
              x: xRight - w,
              y: top,
              width: w,
              height: Math.max(1, h - 1),
            },
            styles: {
              style: "fill",
              color: isPoc
                ? "rgba(220, 38, 38, 0.35)"
                : inValueArea
                  ? "rgba(99, 102, 241, 0.26)"
                  : "rgba(99, 102, 241, 0.12)",
              borderColor: "transparent",
              borderSize: 0,
              borderStyle: "solid",
            },
          });
          const volLabel = volumeLabelFigure(xRight - w, top + h / 2, h, lv.volume);
          if (volLabel) figures.push(volLabel);
        }

        // VAH / VAL dashed lines spanning the bucket, tagged at the right edge.
        if (hasValueArea) {
          const tagX = Math.min(xRight - 2, bounding.width - 4);
          const yVah = yAxis.convertToPixel(profile.vah as number);
          const yVal = yAxis.convertToPixel(profile.val as number);
          for (const [y, text, baseline] of [
            [yVah, `VAH ${fmtPrice(profile.vah as number)}`, "bottom"],
            [yVal, `VAL ${fmtPrice(profile.val as number)}`, "top"],
          ] as const) {
            if (!Number.isFinite(y)) continue;
            figures.push({
              type: "line",
              attrs: {
                coordinates: [
                  { x: xLeft, y },
                  { x: xRight, y },
                ],
              },
              styles: {
                color: "#a855f7",
                size: 1,
                style: "dashed",
                dashedValue: [4, 4],
              },
            });
            figures.push(levelTagFigure(tagX, y, text, "#a855f7", baseline));
          }
        }

        // POC line (the HTP) spanning the week, plus a label at the
        // right edge so the user can read off the numerical price.
        const yPoc = yAxis.convertToPixel(profile.poc);
        if (Number.isFinite(yPoc)) {
          figures.push({
            type: "line",
            attrs: {
              coordinates: [
                { x: xLeft, y: yPoc },
                { x: xRight, y: yPoc },
              ],
            },
            styles: {
              color: "#dc2626",
              size: 1.5,
              style: "solid",
              dashedValue: [],
            },
          });
          figures.push({
            type: "text",
            attrs: {
              x: Math.min(xRight - 2, bounding.width - 4),
              y: yPoc - 2,
              text: `HTP ${fmtPrice(profile.poc)}`,
              align: "right",
              baseline: "bottom",
            },
            styles: {
              color: "#dc2626",
              backgroundColor: "rgba(255,255,255,0.85)",
              borderColor: "#dc2626",
              borderSize: 1,
              paddingLeft: 4,
              paddingRight: 4,
              paddingTop: 1,
              paddingBottom: 1,
              size: 10,
              weight: "bold",
            },
          });
        }
      }
      // Developing POC — amber step-line tracing how the bucket's fair
      // value migrated bar by bar. Points arrive as change-steps; we draw
      // horizontal runs at each level with vertical jumps between them,
      // clamped to the bucket's on-screen span.
      for (const week of weeks) {
        const dev = week.developing;
        if (!dev || dev.length === 0) continue;
        const leftRaw = chart.convertToPixel(
          { timestamp: week.firstBarMs },
          { paneId: "candle_pane" },
        );
        const rightRaw = chart.convertToPixel(
          { timestamp: week.lastBarMs },
          { paneId: "candle_pane" },
        );
        const lc = Array.isArray(leftRaw) ? leftRaw[0] : leftRaw;
        const rc = Array.isArray(rightRaw) ? rightRaw[0] : rightRaw;
        const xLeft = Number(lc?.x);
        const xRight = Number(rc?.x);
        if (!Number.isFinite(xLeft) || !Number.isFinite(xRight)) continue;
        const coords: { x: number; y: number }[] = [];
        for (let i = 0; i < dev.length; i++) {
          const praw = chart.convertToPixel(
            { timestamp: dev[i].ms },
            { paneId: "candle_pane" },
          );
          const pc = Array.isArray(praw) ? praw[0] : praw;
          let x = Number(pc?.x);
          if (!Number.isFinite(x)) continue;
          x = Math.max(xLeft, Math.min(xRight, x));
          const y = yAxis.convertToPixel(dev[i].poc);
          if (!Number.isFinite(y)) continue;
          if (coords.length > 0) {
            // horizontal run at the previous level up to this step's x
            coords.push({ x, y: coords[coords.length - 1].y });
          }
          coords.push({ x, y });
        }
        if (coords.length >= 2) {
          // extend the final level to the bucket's right edge
          coords.push({ x: xRight, y: coords[coords.length - 1].y });
          figures.push({
            type: "line",
            attrs: { coordinates: coords },
            styles: {
              color: "#f59e0b",
              size: 1.5,
              style: "dashed",
              dashedValue: [4, 3],
            },
          });
        }
      }
      return figures;
    },
  });
}

/** Compact volume for histogram row labels: 950, 12.3K, 4.56M, 1.2B. */
function fmtVolume(v: number): string {
  if (!Number.isFinite(v)) return "";
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(a >= 1e10 ? 1 : 2)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(a >= 1e7 ? 1 : 2)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(a >= 1e4 ? 1 : 2)}K`;
  return a >= 10 ? v.toFixed(0) : v.toFixed(1);
}

/**
 * Volume number printed just left of a histogram bar. Skipped when the row
 * is too thin to hold legible text (zoomed out / many rows), so dense
 * profiles don't turn into a smear of overlapping digits.
 */
function volumeLabelFigure(
  xBarLeft: number,
  yMid: number,
  rowHeight: number,
  volume: number,
): OverlayFigure | null {
  if (rowHeight < 9 || volume <= 0) return null;
  return {
    type: "text",
    attrs: {
      x: xBarLeft - 3,
      y: yMid,
      text: fmtVolume(volume),
      align: "right",
      baseline: "middle",
    },
    styles: {
      // Drawn straight onto the chart (no box), so it must follow the theme:
      // slate-600 on the dark canvas was ~2:1 contrast. Read at draw time;
      // overlays redraw on every pan/zoom, so a theme switch lands at once.
      color: document.documentElement.classList.contains("dark")
        ? "#94a3b8"
        : "#475569",
      backgroundColor: "transparent",
      borderColor: "transparent",
      borderSize: 0,
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
      size: Math.min(10, Math.floor(rowHeight - 1)),
      weight: "normal",
    },
  };
}

/** Small right-aligned price tag sitting on a VAH / VAL line. */
function levelTagFigure(
  x: number,
  y: number,
  text: string,
  color: string,
  baseline: "top" | "bottom",
): OverlayFigure {
  return {
    type: "text",
    attrs: {
      x,
      y: baseline === "bottom" ? y - 2 : y + 2,
      text,
      align: "right",
      baseline,
    },
    styles: {
      color,
      backgroundColor: "rgba(255,255,255,0.85)",
      borderColor: color,
      borderSize: 1,
      paddingLeft: 4,
      paddingRight: 4,
      paddingTop: 1,
      paddingBottom: 1,
      size: 10,
      weight: "bold",
    },
  };
}

function fmtPrice(v: number): string {
  if (!Number.isFinite(v)) return "—";
  // Tighter precision for index-style prices, looser for crypto-style.
  if (Math.abs(v) >= 1000) return v.toFixed(2);
  return v.toFixed(4);
}

function formatTimeDelta(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0s";
  const s = Math.round(ms / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins && days === 0) parts.push(`${mins}m`);
  return parts.length > 0 ? parts.join(" ") : `${s}s`;
}
